// src/pages/admin/AuditLogPage.tsx
// ============================================================
// Summit League v2 — Admin Audit Log Page
//
// Unified timeline view of admin_actions + audit_log.
// Filters: source (all/admin/audit), action type, table, search.
// Each entry shows who, what, when, and expandable details.
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axiosClient from '../../api/axiosClient';

import '../../styles/admin-audit.css';

interface AuditEntry {
  id: number;
  source: 'admin_action' | 'audit_log';
  username: string | null;
  user_id: number | null;
  action: string;
  target: string;
  details: Record<string, unknown> | null;
  reason: string | null;
  ip_address: string | null;
  created_at: string;
}

interface Pagination {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
}

interface Filters {
  actionTypes: string[];
  tableNames: string[];
}

export default function AuditLogPage() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [filters, setFilters] = useState<Filters>({ actionTypes: [], tableNames: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter state
  const [source, setSource] = useState<'all' | 'admin_actions' | 'audit_log'>('all');
  const [actionType, setActionType] = useState('');
  const [tableName, setTableName] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  // Expanded entry
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: Record<string, string | number> = { page, limit: 30, source };
      if (debouncedSearch) params.search = debouncedSearch;
      if (actionType) params.action_type = actionType;
      if (tableName) params.table_name = tableName;

      const res = await axiosClient.get('/admin/audit-log', { params });
      setEntries(res.data.data.entries);
      setPagination(res.data.data.pagination);
      setFilters(res.data.data.filters);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [page, source, debouncedSearch, actionType, tableName]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const formatDate = (d: string) => {
    return new Date(d).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', second: '2-digit',
    });
  };

  const formatAction = (action: string) => {
    return action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const toggleExpand = (entryKey: string) => {
    setExpandedId((prev) => (prev === entryKey ? null : entryKey));
  };

  return (
    <div className="sl-admin-audit">
      <div className="sl-admin-page-header">
        <h1 className="sl-admin-page-title">Audit Log</h1>
        {pagination && (
          <span className="sl-admin-page-count">{pagination.totalCount} entries</span>
        )}
      </div>

      {/* Filters */}
      <div className="sl-audit-filters">
        <div className="sl-audit-source-tabs">
          {([
            { value: 'all', label: 'All' },
            { value: 'admin_actions', label: 'Admin Actions' },
            { value: 'audit_log', label: 'System Audit' },
          ] as const).map((s) => (
            <button
              key={s.value}
              className={`sl-audit-source-tab ${source === s.value ? 'sl-audit-source-tab--active' : ''}`}
              onClick={() => { setSource(s.value); setPage(1); setActionType(''); setTableName(''); }}
            >
              {s.label}
            </button>
          ))}
        </div>

        <input
          type="text"
          className="sl-audit-search"
          placeholder="Search by user, action, or target..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {source !== 'audit_log' && filters.actionTypes.length > 0 && (
          <select
            className="sl-audit-filter-select"
            value={actionType}
            onChange={(e) => { setActionType(e.target.value); setPage(1); }}
          >
            <option value="">All Actions</option>
            {filters.actionTypes.map((t) => (
              <option key={t} value={t}>{formatAction(t)}</option>
            ))}
          </select>
        )}

        {source !== 'admin_actions' && filters.tableNames.length > 0 && (
          <select
            className="sl-audit-filter-select"
            value={tableName}
            onChange={(e) => { setTableName(e.target.value); setPage(1); }}
          >
            <option value="">All Tables</option>
            {filters.tableNames.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        )}
      </div>

      {error && <div className="sl-admin-error">{error}</div>}

      {/* Entries */}
      <div className="sl-audit-list">
        {loading && entries.length === 0 ? (
          <div className="sl-admin-loading">Loading audit log...</div>
        ) : entries.length === 0 ? (
          <div className="sl-admin-empty">No audit entries found</div>
        ) : (
          entries.map((entry) => {
            const entryKey = `${entry.source}-${entry.id}`;
            const isExpanded = expandedId === entryKey;
            const hasDetails = entry.details && Object.keys(entry.details).length > 0;

            return (
              <div
                key={entryKey}
                className={`sl-audit-entry ${entry.source === 'admin_action' ? 'sl-audit-entry--admin' : 'sl-audit-entry--system'}`}
              >
                <div
                  className="sl-audit-entry-header"
                  onClick={() => hasDetails && toggleExpand(entryKey)}
                  style={{ cursor: hasDetails ? 'pointer' : 'default' }}
                >
                  <div className="sl-audit-entry-main">
                    <span className={`sl-audit-source-badge ${entry.source === 'admin_action' ? 'sl-audit-source-badge--admin' : 'sl-audit-source-badge--system'}`}>
                      {entry.source === 'admin_action' ? 'Admin' : 'System'}
                    </span>
                    <span className="sl-audit-action">{formatAction(entry.action)}</span>
                    <span className="sl-audit-target">{entry.target}</span>
                  </div>
                  <div className="sl-audit-entry-meta">
                    <span className="sl-audit-user">{entry.username || 'system'}</span>
                    <span className="sl-audit-date">{formatDate(entry.created_at)}</span>
                    {hasDetails && (
                      <span className="sl-audit-expand-icon">{isExpanded ? '▾' : '▸'}</span>
                    )}
                  </div>
                </div>

                {entry.reason && (
                  <div className="sl-audit-reason">{entry.reason}</div>
                )}

                {isExpanded && hasDetails && (
                  <div className="sl-audit-details">
                    <pre className="sl-audit-details-pre">
                      {JSON.stringify(entry.details, null, 2)}
                    </pre>
                    {entry.ip_address && (
                      <div className="sl-audit-ip">IP: {entry.ip_address}</div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="sl-audit-pagination">
          <button
            className="sl-audit-pagination-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
          >
            ← Prev
          </button>
          <span className="sl-audit-pagination-info">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            className="sl-audit-pagination-btn"
            onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            disabled={page >= pagination.totalPages || loading}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
